<template>
  <div class="security-seal" :class="{ 'security-seal--small': small }">
    <!-- Filled by TrustLogo on live. The static image is both the non-live
         placeholder and what shows until the script has loaded, so the seal
         never leaves an empty gap. -->
    <div v-if="isLive" ref="sealContainer">
      <img
        v-if="!rendered"
        src="~/assets/images/sectigo-trust-seal.png"
        alt="Secured by Sectigo"
        width="106"
        height="42"
      />
    </div>
    <img
      v-else
      src="~/assets/images/sectigo-trust-seal.png"
      alt="Secured by Sectigo"
      width="106"
      height="42"
    />
  </div>
</template>

<script>
// Sectigo TrustLogo site seal for our EV certificate (*.pedalsure.com).
//
// Sectigo's embed snippet is two inline scripts that rely on document.write,
// which only works while the page is still parsing — called from a mounted
// component it would wipe the whole document. So, like sectigo.com's own
// seal component, we load trustlogo.js once, then briefly swap document.write
// for a collector while TrustLogo() runs and put what it wrote into our
// container. Live only: the seal verifies against location.host, so on stage
// or local it would show a failed verification.
const TRUSTLOGO_SRC =
  'https://secure.trust-provider.com/trustlogo/javascript/trustlogo.js'
const SEAL_IMAGE =
  'https://www.sectigo.com/images/seals/sectigo_trust_seal_md_2x.png'
const CERTIFICATE_TYPE = 'SECEV'

let scriptPromise = null
function loadTrustLogo() {
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.async = true
      script.src = TRUSTLOGO_SRC
      script.onload = resolve
      script.onerror = () => {
        scriptPromise = null
        reject(new Error('trustlogo.js failed to load'))
      }
      document.head.appendChild(script)
    })
  }
  return scriptPromise
}

export default {
  name: 'SecuritySeal',
  props: {
    // 82px wide instead of 106px, for tight spots like the quote panel.
    small: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return { rendered: false }
  },
  computed: {
    isLive() {
      return this.$config.envType === 'live'
    },
  },
  mounted() {
    if (!this.isLive) return
    // trustlogo.js and the images it pulls in (~20 KiB, plus its own
    // long tasks) only load once the seal is near the viewport. On the
    // homepage it sits well below the fold, so a first view never pays for
    // it; the static placeholder covers the seal until then.
    if (typeof window.IntersectionObserver !== 'function') {
      this.renderTrustLogo()
      return
    }
    this.observer = new window.IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return
        this.disconnectObserver()
        this.renderTrustLogo()
      },
      { rootMargin: '200px' }
    )
    this.observer.observe(this.$el)
  },
  beforeDestroy() {
    this.disconnectObserver()
  },
  methods: {
    disconnectObserver() {
      if (this.observer) {
        this.observer.disconnect()
        this.observer = null
      }
    },
    renderTrustLogo() {
      loadTrustLogo()
        .then(() => {
          if (this._isDestroyed || typeof window.TrustLogo !== 'function')
            return
          const originalWrite = document.write
          let html = ''
          document.write = (str) => {
            html += str
          }
          try {
            window.TrustLogo(SEAL_IMAGE, CERTIFICATE_TYPE, 'none')
          } finally {
            document.write = originalWrite
          }
          if (html && this.$refs.sealContainer) {
            this.rendered = true
            this.$nextTick(() => {
              const container = this.$refs.sealContainer
              if (!container) return
              container.innerHTML = html
              // TrustLogo's <img> has no alt or size, which fails the alt
              // and explicit width/height audits. Same values as the
              // placeholder; CSS still sets the rendered width.
              container.querySelectorAll('img').forEach((img) => {
                if (!img.hasAttribute('alt')) img.alt = 'Secured by Sectigo'
                if (!img.hasAttribute('width')) img.width = 106
                if (!img.hasAttribute('height')) img.height = 42
              })
            })
          }
        })
        // A blocked or failed script leaves the static seal in place.
        .catch(() => {})
    },
  },
}
</script>

<!-- Not scoped: the <img> TrustLogo writes has no scope attribute, and the
     seal image is 2x, so it needs this rule to render at its intended size. -->
<style lang="scss">
.security-seal {
  width: 106px;
  min-height: 42px;

  img {
    display: block;
    width: 106px;
    height: auto;
  }

  &--small {
    width: 82px;
    min-height: 32px;

    img {
      width: 82px;
    }
  }
}
</style>
